import React, { useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAgentContext } from '@Providers';

import { withValidPermission } from '@Components';
import { Permission } from '@Models';
import { useAgents } from '../../CustomHooks';
import { useRedeemJourneyToken } from '../../CustomHooks/Agents';
import { isValidUuid } from '../../Helpers/Agents/uuid';

const INITIAL_MESSAGE_MAX_LENGTH = 1000;

// Module-level set of tokens currently being redeemed. Survives component
// remounts (StrictMode double-mount, route navigation) so the same token
// isn't POSTed twice concurrently. Cleared in `.finally` so legitimate
// revisits still re-trigger redemption — the backend is idempotent and
// returns the existing conversation_id on already-consumed tokens.
const inFlightTokens = new Set<string>();

const AgentPageContent: React.FC = () => {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const [searchParams] = useSearchParams();
  const { setActiveChatId, setIsOpen, setMode, setSelectedAgent, setInitialMessage } = useAgentContext();
  const { data: agents } = useAgents();
  const { mutateAsync: redeemToken } = useRedeemJourneyToken();

  // Handle direct /agent/:conversationId URL.
  useEffect(() => {
    const validId = isValidUuid(conversationId) ? conversationId : null;
    setActiveChatId(validId);
    setIsOpen(true);
    setMode('fullscreen');
  }, [conversationId, setActiveChatId, setIsOpen, setMode]);

  // Handle /agent?journey_token=xxx — waits for agents before redeeming
  // so setSelectedAgent fires before the chat opens, preventing auto-selection of the wrong agent.
  const journeyToken = searchParams.get('journey_token');

  useEffect(() => {
    if (!journeyToken) return;
    if (!agents?.length) return;

    const sanitized = journeyToken.replace(/[^a-f0-9-]/gi, '').substring(0, 36);
    if (!isValidUuid(sanitized)) return;
    if (inFlightTokens.has(sanitized)) return;

    inFlightTokens.add(sanitized);

    redeemToken(sanitized)
      .then(({ conversation_id, source_name }) => {
        if (source_name && agents?.length) {
          const matchingAgent = agents.find((a) => a.source_name === source_name);
          if (matchingAgent) setSelectedAgent(matchingAgent);
        }
        setActiveChatId(conversation_id);
        setIsOpen(true);
        setMode('fullscreen');
      })
      .catch(() => {
        // Token redemption failed (expired, wrong email, etc.)
      })
      .finally(() => {
        inFlightTokens.delete(sanitized);
      });
  }, [journeyToken, agents, redeemToken, setActiveChatId, setIsOpen, setMode, setSelectedAgent]);

  // /agent?q=mensaje — auto-send once an agent is selected.
  const initialQuery = searchParams.get('q');
  const hasHandledQueryRef = useRef(false);

  useEffect(() => {
    if (!initialQuery || hasHandledQueryRef.current) return;
    hasHandledQueryRef.current = true;
    setInitialMessage(initialQuery.slice(0, INITIAL_MESSAGE_MAX_LENGTH));
  }, [initialQuery, setInitialMessage]);

  return null;
};

// Gate the /agent route on the access:agent permission, same as the other
// permission-gated pages (Suppliers, Integrations, Users): renders Loading
// while permissions resolve and navigates to /notfound when the user lacks it.
// The flavor is already enforced by the route's prodEnv guard in app.tsx.
export default withValidPermission(AgentPageContent, Permission.AccessAgent);
